import angular from "angular";

import { Arrays, Calculation, CalculationListItem, CalculationRowType, Calculations, CalculatorLogic, Message, MessageFrom, MessageListItem, MessageThread, MessageThreads, MessageThreadStatus, Numeric, ODataResult, PayrollListItem, Payrolls, ThreadedMessageType, Translations } from "@salaxy/core";

import { EditDialogKnownActions, SessionService, UiHelpers, UploadService } from "../../services";
import { ApiCrudObjectController } from "../bases";

/**
 * User interface logic for a set of messages and/or attachments
 * that are grouped together as messages thread.
 */
export class MessageThreadCrudController extends ApiCrudObjectController<MessageThread, MessageListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["MessageThreads", "UiHelpers", "UploadService", "$location", "$routeParams", "$anchorScroll", "$timeout", "SessionService", "Calculations", "Payrolls"];

  /** New message that is edited / to be added to the thread. */
  public newMessage: Message = {
    type: ThreadedMessageType.Text,
  };

  /** If true, the message is currently in edit mode. */
  public isInEdit: boolean;

  /** Role of the current user. Owner is default. */
  public role: MessageFrom;

  /** Once message thread loaded, create a calculation based on the rows. */
  public createCalc: boolean;

  /** Calculation that is created / updated based on the message thread rows. */
  public calc: Calculation;

  /** Approval page url. */
  public approvalUrl: string;

  /**
   * If set, the controller will mark a message thread as open once it has been loaded.
   * If the value is true, the message is marked as read immediately.
   * If the value is number, the controller waits the given amount of seconds before marking the item as read.
   */
  private _markAsRead: number | true;

  private _otherParty: string;

  private _otherPartyOptions: { [key: string]: string };

  private markAsReadTimeout: any;

  constructor(
    private messageApi: MessageThreads,
    uiHelpers: UiHelpers,
    protected uploadService: UploadService,
    $location: angular.ILocationService,
    $routeParams: any,
    private $anchorScroll: angular.IAnchorScrollService,
    private $timeout: angular.ITimeoutService,

    private sessionService: SessionService,

    private calculations: Calculations,
    private payrolls: Payrolls,
  ) {
    // Dependency injection
    super(messageApi, uiHelpers, $location, $routeParams);
  }
  /**
   * Initialization code.
   */
  public $onInit() {
    this.role = this.role || MessageFrom.Owner;
    super.$onInit();
  }

  /**
   * Cleaning the timeout.
   */
  public $onDestroy = () => {
    if (angular.isDefined(this.markAsReadTimeout)) {
      this.$timeout.cancel(this.markAsReadTimeout);
    }
  };

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/messages",
      detailsUrl: this.detailsUrl || "/messages/details/",
      approvalUrl: this.approvalUrl || "/messages/approval/",
      oDataTemplateUrl: "salaxy-components/odata/lists/MessageThreads.html",
      oDataOptions: {},
    };
  }

  /**
   * If true, this is a new unsaved object.
   * Adds "new-for-calc" and "new-for-payroll" actions as new item.
   */
   public isNew(): boolean {
    if (this.parentController) {
      return this.parentController.isNew();
    }
    return this.action === "new-for-calc" || this.action === "new-for-payroll" || super.isNew();
  }

  /**
   * Overrides the isReadOnly with workflow cases
   */
  public get isReadOnly(): boolean {
    return super.isReadOnly ||
      (this.role === "owner" && this.current?.workflowData?.events.some((x) => x.type === "PartnerMessageClosed"));
  }

  /**
   * If set, the controller will mark a message thread as open once it has been loaded.
   * If the value is true, the message is marked as read immediately.
   * If the value is number, the controller waits the given amount of seconds before marking the item as read.
   */
  public get markAsRead(): number | true {
    if (this.parentController) {
      return (this.parentController as MessageThreadCrudController).markAsRead;
    }
    return this._markAsRead;
  }
  public set markAsRead(value: number | true) {
    if (this.parentController) {
      (this.parentController as MessageThreadCrudController).markAsRead = value;
    }
    this._markAsRead = value;
  }

  /**
   * Salaxy ID (IBAN format) for the other party (besides the message thread owner).
   * Supports special values: "partner" for payment partner.
   */
  public get otherParty(): string {
    return this._otherParty;
  }
  public set otherParty(value: string) {
    if (value === "partner") {
      this._otherParty = this.sessionService.session.settings.partner.accountId;
    } else {
      this._otherParty = value;
    }
  }

  /**
   * If set, shows a drop-down for these options using the enum component.
   * Keys may have special values: "partner": payment partner.
   */
  public get otherPartyOptions(): { [key: string]: string } {
    return this._otherPartyOptions;
  }
  public set otherPartyOptions(value: { [key: string]: string }) {
    if (!value || Object.keys(value).length === 0) {
      this._otherPartyOptions = null;
      return;
    }
    this._otherPartyOptions = Object.assign({}, ...Object.keys(value).map((key) => {
      if (key === "partner") {
        const partner = this.sessionService.session.settings.partner;
        return { [partner.accountId]: value[key] || partner.serviceModel.site.webAppName || partner.info.avatar.displayName };
      }
      return { [key]: value[key] };
    }));
  }

  /**
   * Returns true if the other-party is 'partner' or partner's ID.
   */
  public get isOtherPartyPartner(): boolean {
    return this.otherParty === "partner" ||  this.otherParty === this.sessionService.session?.settings?.partner?.accountId;
  }

  /**
   * Adds a text message (from the textarea / this.newMessage) to the current thread.
   */
  public sendMessage(): Promise<MessageThread> {
    this.newMessage.content = (this.newMessage.content || "").trim();
    this.newMessage.fromParty = this.role;
    switch (this.newMessage.type) {
      case ThreadedMessageType.Text:
        if (!this.newMessage.content) {
          return;
        }
        break;

    }
    return this.setStatus(this.messageApi.addMessage(this.currentId, this.newMessage))
      .then((thread) => {
        this.newMessage = {
          type: ThreadedMessageType.Text,
        };
        this.setCurrent(thread);
        return thread;
      });
  }

  /** Sets the type of the message to be added / edited */
  public setType(type: ThreadedMessageType) {
    type = type || ThreadedMessageType.Text;
    this.newMessage = {
      type: type,
    };
    switch (type) {
      case "absence" as any:
        this.newMessage.contentData = {
          periods: [],
        };
        this.newMessage.content = "Absence content";
        break;
      case "annualLeave" as any:
        this.newMessage.contentData = {
          leaves: [],
        };
        this.newMessage.content = "Annual leave content";
        break;
    }
  }

  /**
   * Sends an email notification to the thread owner.
   */
  public sendNotification(): Promise<MessageThread> {
    const endUserService = this.sessionService.session.settings.partner.serviceModel.site.webAppName || this.sessionService.session.settings.partner.info.avatar.displayName;
    const subject = `${Translations.get("SALAXY.NG1.MessageThreadComponent.sendNotificationSubject")} ${endUserService}`;
    const body = `${Translations.get("SALAXY.NG1.MessageThreadComponent.sendNotificationBody")}`;

    return this.setStatus(this.messageApi.sendNotification(this.currentId, subject, body))
      .then((thread) => {
        this.setCurrent(thread);
        return thread;
      });
  }

  /** Overrides the save to assure that the other party is defined (can be set in component properties).  */
  public save() {
    if (!this.current.otherParty) {
      if (!this.otherParty) {
        this.uiHelpers.showAlert("SALAXY.NG1.MessageThreadComponent.saveNoToField");
        return;
      }
      this.current.otherParty = this.otherParty;
    }
    return super.save();
  }

  /** Selects a calculation as current calculation. */
  public selectCalc(): void {
    this.uiHelpers.openSelectCalcs("draft", "Valitse palkkalaskelma").then((dialogResult) => {
      if (dialogResult.action == EditDialogKnownActions.Ok && dialogResult.item.length > 0) {
        this.calculations.getSingle(dialogResult.item[0].id).then((calc) => {
          this.calc = calc;
          this.updateCalc();
        });
      }
    });
  }

  /** Updates the calculation based on rows in the message. */
  public updateCalc(): void {
    this.calc = this.calc || CalculatorLogic.getBlank();
    const totalMinutes = Arrays.sum(this.current.messages, (x) => x.contentData?.totalTime || 0);
    this.calc.employer.isSelf = true;
    this.calc.worker.accountId = this.calc.worker.accountId || this.current.otherParty;
    this.calc.rows = this.calc.rows.filter((x) => x.sourceId != this.current.id);
    // HACK: Get the price from employment
    const price = 10;
    this.calc.rows.splice(0, 0, {
      rowType: CalculationRowType.HourlySalary,
      count: Numeric.round(totalMinutes / 60),
      price,
      sourceId: this.current.id,
    });
    // TODO: Lauantai ja sunnuntai
    this.recalculate();
  }

  /** Recalculates the calculation based on the rows in the message. */
  public recalculate(): Promise<Calculation> {
    return this.calculations.recalculate(this.calc).then((result) => {
      this.calc = result;
      return this.calc;
    });
  }

  /** Saves the current calculation. */
  public saveCalc(): Promise<Calculation> {
    return this.calculations.save(this.calc).then((result) => {
      this.calc = result;
      return this.calc;
    });
  }

  /**
   * Starts the reload process depending on the bindingMode.
   * Overrides a different load process for "new-for-calc" and "new-for-payroll".
   * Overrides the reload to send the browser to the bottom
   */
   public reload(): Promise<MessageThread> {
    if (this.action === "new-for-calc" || this.action === "new-for-payroll") {
      if (this.parentController) {
        return this.parentController.reload().then((item) => {
          this._currentId = null;
          return item;
        });
      } else {

        const prepareMessage = (): Promise<MessageThread> => {
          const newMessage = this.api.getBlank();
          newMessage.status = MessageThreadStatus.Active;
          if (this.currentId === "new") {
            this._currentId = null;
          }
          if (this.action === "new-for-calc" && this.currentId && this.currentId != "new") {

            return this.getCalculationListItem(this.currentId).then( (calculationListItem) => {
              newMessage.title = "Palkka " + calculationListItem?.otherPartyInfo?.avatar?.displayName;
              newMessage.ownerWorkflow = {
                salaryDate: calculationListItem?.salaryDate
              };
              newMessage.messages.push(
                {
                  contentId: this.currentId,
                  fromParty: this.role || MessageFrom.Owner,
                  type: ThreadedMessageType.Calculation
                });
                return newMessage;
              });
          }
          else if (this.action === "new-for-payroll" && this.currentId && this.currentId != "new")
          {
            return this.getPayrollListItem(this.currentId).then( (payrollListItem) => {
              newMessage.status = MessageThreadStatus.Active;
              newMessage.title = "Palkkalista " + payrollListItem?.shortText;
              newMessage.ownerWorkflow = {
                salaryDate: payrollListItem?.salaryDate
              };
              newMessage.messages.push(
                {
                  contentId: this.currentId,
                  fromParty: this.role || MessageFrom.Owner,
                  type: ThreadedMessageType.Payroll
                });
                return newMessage;
              });
          } else {
            return Promise.resolve(newMessage);
          }
        };
        return prepareMessage().then( (newMessage) => {
          this._currentId = null;
          return this.setStatus(Promise.resolve(newMessage))
            .then((item) => {
              this.setCurrentRef(item);
              return item;
            });
        });
      }
    } else {
      return super.reload().then((value) => {

        // HACK?
        if (this.createCalc) {
          this.updateCalc();
        }
        if (!this.isNew()) {
          this.$timeout(() => {
            this.$anchorScroll("messageContainerBottom");
          });
          if (this.status === "loaded" && this.markAsRead && !this.getReadTime()) {
            const relevantMessages = this.current.messages
              .filter((x) => x.fromParty === (this.role === "otherParty" ? "owner" : "otherParty"));
            if (relevantMessages.length > 0) {
              if (this.markAsRead === true) {
                this.markAllAsRead();
              } else {
                this.markAsReadTimeout = this.$timeout(() => {
                  if (!this.isInEdit) {
                    this.markAllAsRead();
                  }
                }, this.markAsRead * 1000);
              }
            }
          }
        }
        return value;
      });
    }
  }

  /**
   * Marks all messages as read by a given party.
   * The command is immediately sent to the server and the current message is updated incrementally (just the read/unread info).
   * @param readBy The party for which the messages are marked as read.
   * If not specified, uses the current role or the controller.
   * @returns The entire thread with the messages marked as read.
   */
  public markAllAsRead(readBy?: MessageFrom): Promise<MessageThread> {
    readBy = readBy || this.role || MessageFrom.Owner;
    return this.messageApi.markAsRead(this.currentId, readBy)
      .then((result) => {
        this.current.messages.forEach((msg: Message) => {
          if (readBy === "otherParty") {
            msg.readByOtherParty = msg.readByOtherParty || new Date().toISOString();
          } else {
            msg.readByOwner = msg.readByOwner || new Date().toISOString();
          }
        });
        return result;
      });
  }

  /**
   * Marks a message / attachment as read/unread also changing all messages before that message.
   * The role of the read/unread is determined by the message: It is the opposite of fromParty (owner (default)/otherParty).
   * The changes are made only to the current message, not Saved to the server.
   * @param message The message that is marked as read/unread (with the messages before that).
   * @param readByValue Possibility to force whether the flag is set to read or unread.
   * If null, the flag is set to the opposite of the current.
   */
  public markItemAsRead(message: Message, readByValue: boolean = null) {
    const flagFor: "owner" | "otherParty" = message.fromParty === "otherParty" ? "owner" : "otherParty";
    if (readByValue == null) {
      readByValue = flagFor === "owner" ? !message.readByOwner : !message.readByOtherParty;
    }
    if (readByValue) {
      for (const item of this.current.messages) {
        if (flagFor === "owner") {
          item.readByOwner = item.readByOwner || new Date().toISOString();
        } else {
          item.readByOtherParty = item.readByOtherParty || new Date().toISOString();
        }
        if (item === message) {
          return;
        }
      }
    } else {
      const startFrom = this.current.messages.indexOf(message);
      for (let i = startFrom; i < this.current.messages.length; i++) {
        if (flagFor === "owner") {
          this.current.messages[i].readByOwner = null;
        } else {
          this.current.messages[i].readByOtherParty = null;
        }
      }
    }
  }

  /**
   * Gets the time when a message was read.
   * The time is dependent of the fromParty of the message: There are separate times for owner and other party.
   * If time is null, the message has not been read.
   * @param message Message for which the readTime is fetched.
   * If not set, uses the last message for the current party.
   * @returns If fromParty is "otherParty", then readByOwner is returned, otherwise readByOtherParty is returned.
   * The value should be ISO date (string) or null if the item has not been read yet.
   */
  public getReadTime(message: Message = null) {
    if (!message) {
      const relevantMessages = this.current.messages
        .filter((x) => x.fromParty === (this.role === "otherParty" ? "owner" : "otherParty"));
      if (relevantMessages.length === 0) {
        return null;
      }
      message = relevantMessages[relevantMessages.length - 1];
    }
    if (message.fromParty === "otherParty") {
      return message.readByOwner || null;
    } else {
      return message.readByOtherParty || null;
    }
  }

  /** temp upload progress */
  public uploadProgress = 0;

  /**
   * Uploads the files to the server
   * @param files The files collection from the upload component
   * @example
   * <button ngf-select="$ctrl.uploadFiles($files)" multiple accept="image/*">Select Files</button>
   */
  public uploadFiles(files: any[]) {
    if (files && files.length) {
      const loader = this.uiHelpers.showLoading("SALAXY.NG1.MessageThreadComponent.uploadFilesSaving");
      this.uploadService.upload<MessageThread>(
        this.messageApi.getUploadUrl(this.currentId), {
          files,
          from: this.role || "owner",
        }, (progress, error) => {
          this.uploadProgress = progress;
        }).then((response) => {
          this.newMessage.content = "";
          this.setCurrent(response);
          loader.dismiss();
          return response;
        }).catch((response) => {
            loader.dismiss();
            this.uiHelpers.showAlert("SALAXY.UI_Terms.error", "SALAXY.NG1.MessageThreadComponent.uploadFilesError");
            throw new Error(response);
        });
    }
  }

  /**
   * Gets the file display related information.
   * @param message Message that contains the file.
   */
  public getFileInfo(message: Message) {
    // TODO: Move to a helper
    const result = {
      isImage: false,
      previewUrl: null,
      downloadUrl: null,
      icon: null,
    };
    if (message.type !== "blobFile") {
      return result;
    }
    result.isImage = true;
    result.icon = this.uploadService.getFileIcon(message.content);
    if (message.contentId) {
      if (message.preview === message.contentId) {
        result.previewUrl = this.uploadService.getPreviewUrl(message.preview);
      }
      result.downloadUrl = this.uploadService.getFileUrl(message.contentId);
    }
    return result;
  }

  /**
   * Opens the list of calculations into a dialog window for selection and then adds them to payroll
   * @param category Either "paid": Read-only, a copy is added ... or "draft": Editable, moved with status changed.
   */
  public addExistingCalc(category: "paid" | "draft" = "draft") {
    const calculations = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/CalcList.html",
      calculations,
      {
        title: (category === "paid" ? "SALAXY.NG1.MessageThreadComponent.addExistingCalcCopy" : "SALAXY.NG1.MessageThreadComponent.addExistingCalcMove"),
        category,
      }).then((result) => {
        if (result.action === "ok" && calculations.length > 0) {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.MessageThreadComponent.addExistingCalcLoading");
          const messages = calculations.map((x) => {
            const msg: Message = {
              contentId: x.id,
              fromParty: this.role || MessageFrom.Owner,
              type: ThreadedMessageType.Calculation,
            };
            return msg;
          });
          this.current.messages.push(...messages);
          this.save().then(() => {
            loader.dismiss();
          });
        }
      });
  }

  /**
   * Opens the list of payrolls into a dialog window for selection and then adds them to payroll
   * @param category Either "paid": Read-only, a copy is added ... or "draft": Editable, moved with status changed.
   */
   public addExistingPayroll(category: "paid" | "draft" = "draft") {
    const payrolls = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/payroll/PayrollList.html",
      payrolls,
      {
        title: (category === "paid" ? "Maksetut" : "Luonnokset"),
        category,
      }).then((result) => {
        if (result.action === "ok" && payrolls.length > 0) {
          const loader = this.uiHelpers.showLoading("Lisätään palkkalistoja");
          const messages = payrolls.map((x) => {
            const msg: Message = {
              contentId: x.id,
              fromParty: this.role || MessageFrom.Owner,
              type: ThreadedMessageType.Payroll,
            };
            return msg;
          });
          this.current.messages.push(...messages);
          this.save().then(() => {
            loader.dismiss();
          });
        }
      });
  }

  /**
   * Navigates to the business object details page
   * @param item The message item.
   * @param defaultMessageDetailsUrl The URL for the default message details page.
   */
  public navigateToBusinessObject(item: MessageListItem, defaultMessageDetailsUrl: string) {
    return this.getApi().getSingle(item.id).then( (messageThread: MessageThread) => {
      const message = messageThread.messages?.find((x) => !!x.contentId && x.type === "payroll");
      if (message) {
        this.$location.path("payroll/details/" + message.contentId);
      }
      else {
        this.$location.path(defaultMessageDetailsUrl + item.id);
      }
    });
  }

  /** Deletes a message from the message list (must be saved still) */
  public deleteMessage(item: Message) {
    this.current.messages.splice(this.current.messages.findIndex((x) => x === item), 1);
  }

  private getCalculationListItem(id: string): Promise<CalculationListItem> {
    return this.calculations.getOData({ $filter: `id eq '${id}'`}).then( (data: ODataResult<CalculationListItem>) => {
      if (data.items.length == 1) {
        return data.items[0];
      } else {
        return null;
      }
    });
  }
  private getPayrollListItem(id: string): Promise<PayrollListItem> {
    return this.payrolls.getOData({ $filter: `id eq '${id}'`}).then( (data: ODataResult<PayrollListItem>) => {
      if (data.items.length == 1) {
        return data.items[0];
      } else {
        return null;
      }
    });
  }
}
